import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';

import { storeName as layoutModelStore } from './layout.model';
import { storeName as projectModelStore } from './project.model';
import { storeName as sitePlanModelStore } from './sitePlan.model';
import { RootModel } from '../models';
import { storeName as floorEntityStore } from '../entities/floor.entity';
import { storeName as projectChildItemEntityStore } from '../entities/projectChildItem.entity';
import { storeName as promotionEntityStore } from '../entities/promotion.entity';
import { storeName as questionsEntityStore } from '../entities/question.entity';
import { storeName as projectItemEntityStore } from '../entities/projectItem.entity';
import { storeName as projectEntityStore } from '../entities/project.entity';
import { storeName as tourEntityStore } from '../entities/tour.entity';
import { storeName as userContactEntity } from '../entities/userContact.entity';

import {
  FloorTypes,
  ProjectChildItemTypes,
  ProjectItemTypes,
  ProjectTypes,
  SimplifiedFloorTypes,
  ShowroomTypes,
  UserContactTypes,
  QuestionType,
} from '../../types';
import {
  CONTENT_KEY,
  HOME_PROFILE_FEATURE_ACTIONS,
  HOME_PROFILE_PAGE_LINK,
  PROJECT_ITEM_TYPE,
  SELF_HOME_PROFILE_LANGUAGE_PATH,
  SELF_HOME_PROFILE_PAGE_LINK,
} from '../../commons/constants';
import { buildHashUrl, getHashParams } from '../../utils';
import { PromotionResponseTypes, PromotionsTypes } from '../../types/response/promotion';

export const storeName = 'homeProfileModel';

export type PromotionDetailTypes = {
  parentCode: string;
  type: string;
  code: string;
};

type HomeProfileModelTypes = {
  homeId: number;
  homeCode: string;
  homeKey: string;
  showroomData: ShowroomTypes[];
  viewDetailShowroom: number;
  openContent: string;
  promotionCodes: string[];
  promotionDetail: PromotionDetailTypes;
  isShowPromotionTop: boolean;
  isShowPromotionBottom: boolean;
  isSelf: boolean; // flag check current home profile is self home profile
  isShowAgents: boolean;
  userContactCodes: string[];
  isInViewTabPanel: boolean; // flag check tab panel in homeprofile is pinned or not
  isOpenPromotionPopup: boolean;
  currentActiveAgent: string | null;
  // flag manage layout state homeprofile (true - homeprofile is 1 column, false - homeprofile is 2 column)
  isOneColumn: boolean;
  askedQuestionCodes: string[];
  loading: boolean;
};

const initialState: HomeProfileModelTypes = {
  homeId: null,
  homeCode: null,
  homeKey: null,
  showroomData: null,
  viewDetailShowroom: null,
  openContent: null,
  promotionCodes: null,
  promotionDetail: null,
  isShowPromotionTop: false,
  isShowPromotionBottom: false,
  isSelf: false,
  isShowAgents: false,
  userContactCodes: null,
  isInViewTabPanel: false,
  isOpenPromotionPopup: false,
  currentActiveAgent: null,
  isOneColumn: false,
  askedQuestionCodes: [],
  loading: true,
};

export const homeProfileModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setHomeData: produce(
      (state, payload: { id: number; code: string; key: string; isSelf: boolean }) => {
        if (!payload) {
          state.homeId = initialState.homeId;
          state.homeCode = initialState.homeCode;
          state.homeKey = initialState.homeKey;
          state.isSelf = initialState.isSelf;
          return;
        }

        state.homeId = payload.id;
        state.homeCode = payload.code;
        state.homeKey = payload.key;
        state.isSelf = payload.isSelf || false;
      }
    ),
    setShowroomData: produce((state, payload: ShowroomTypes[]) => {
      if (!payload) {
        state.showroomData = initialState.showroomData;
        return;
      }

      state.showroomData = payload;
    }),
    setViewDetailShowroomId: produce((state, payload: number) => {
      if (!payload) {
        state.viewDetailShowroom = initialState.viewDetailShowroom;
        return;
      }

      state.viewDetailShowroom = payload;
    }),
    setOpenContent: produce((state, payload: string) => {
      if (!payload) {
        state.openContent = initialState.openContent;
        return;
      }

      state.openContent = payload;
    }),
    setPromotionCodes: produce((state, payload: string[]) => {
      if (!payload) {
        state.promotionCodes = initialState.promotionCodes;
        return;
      }
      state.promotionCodes = payload;
    }),
    setPromotionTop: produce((state, payload: boolean) => {
      state.isShowPromotionTop = payload;
    }),
    setPromotionBottom: produce((state, payload: boolean) => {
      state.isShowPromotionBottom = payload;
    }),
    setPromotionDetail: produce((state, payload: PromotionDetailTypes) => {
      if (!payload) {
        state.isOpenPromotionPopup = false;
        state.promotionDetail = initialState.promotionDetail;
        return;
      }
      state.promotionDetail = payload;
    }),
    setShowAgents: produce((state, payload: boolean) => {
      state.isShowAgents = payload;
    }),
    setInViewStatus: produce((state: HomeProfileModelTypes, payload: boolean) => {
      state.isInViewTabPanel = payload;
    }),

    setUserContactCodes: produce((state, payload: string[]) => {
      if (!payload) {
        state.userContactCodes = initialState.userContactCodes;
        return;
      }
      state.userContactCodes = payload;
    }),
    setStatusPromotionPopup: produce((state, payload) => {
      state.isOpenPromotionPopup = payload;
    }),
    setActiveAgent: produce((state, payload) => {
      state.currentActiveAgent = payload;
    }),
    setHomeProfileLayoutState: produce((state, payload) => {
      state.isOneColumn = payload;
    }),
    setAskedQuestionCodes: produce((state, payload) => {
      if (!payload) {
        state.askedQuestionCodes = initialState.askedQuestionCodes;
      }
      state.askedQuestionCodes = payload;
    }),

    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    updateOpenContent(payload, state) {
      if (!payload || state[storeName].openContent === payload) {
        this.setOpenContent();
        return;
      }

      this.setOpenContent(payload);
    },
    setHomeProfileRedirectData(payload, state) {
      const {
        id = state[storeName].homeId,
        code = state[storeName].homeCode,
        key = state[storeName].homeKey,
        isSelf = state[storeName].isSelf,
        projectKey = state[projectModelStore].projectKey,
        projectItemKey = state[sitePlanModelStore].projectItemKey,
        floorKey = state[sitePlanModelStore].floorKey,
        history,
      } = payload;

      let { link } = payload;
      if (!state[storeName].isSelf && !link) link = HOME_PROFILE_PAGE_LINK;

      this.setHomeData({ id, code, key, isSelf });
      dispatch[sitePlanModelStore].setProjectChildItemId();

      if (history && state[storeName].isSelf) {
        if (!link) link = SELF_HOME_PROFILE_PAGE_LINK;
        const { displayName } = state[layoutModelStore].locale;
        if (!displayName) return;

        history.push(
          link
            .replace(':languagePath', SELF_HOME_PROFILE_LANGUAGE_PATH[displayName])
            .replace(':itemKey', key)
        );
        return;
      }

      let extendable = key;
      if (floorKey) extendable = `${floorKey}--${key}`;

      const homeProfileLink = dispatch[projectModelStore]
        .getHomePageLink({ link })
        .replace(':projectKey', projectKey)
        .replace(':itemKey', projectItemKey)
        .replace(':extendable', extendable);
      if (history && !!homeProfileLink) history.push(homeProfileLink);
    },
    setPromotionRedirectData(payload, state) {
      const {
        key = state[storeName].homeKey,
        projectKey = state[projectModelStore].projectKey,
        projectItemKey = state[sitePlanModelStore].projectItemKey,
        floorKey = state[sitePlanModelStore].floorKey,
        link = HOME_PROFILE_PAGE_LINK,
        history,
      } = payload;

      let extendable = key;
      if (floorKey) extendable = `${floorKey}--${key}`;
      const typeOF = (value) => Object.prototype.toString.call(value).slice(8, -1);
      typeOF(extendable);

      const homeProfileLink = dispatch[projectModelStore]
        .getHomePageLink({ link })
        .replace(':projectKey', projectKey)
        .replace(':itemKey', projectItemKey)
        .replace(':extendable', extendable);
      if (history && !!homeProfileLink) history.push(homeProfileLink);
    },

    goBackRedirectionData(payload, state) {
      const { history } = payload;
      let { homeKey = state[storeName].homeKey } = payload;

      const homeData: ProjectChildItemTypes =
        state[projectChildItemEntityStore].ids[state[storeName].homeId];

      // get original project child item alias
      // overwrite home key with home code when home key is assigned user contact key
      if (homeData && homeData.userContact) {
        switch (state[sitePlanModelStore].projectItemType) {
          case PROJECT_ITEM_TYPE.VILLA_BLOCK:
            const villaBlock: ProjectItemTypes =
              state[projectItemEntityStore].ids[state[sitePlanModelStore].projectItemId];

            let villa: ProjectChildItemTypes;
            if (villaBlock) {
              villa = (villaBlock.items as ProjectChildItemTypes[]).find(
                (i) => i.id === state[storeName].homeId
              );

              if (villa) {
                homeKey = villa.alias;
                dispatch[projectChildItemEntityStore].saveSimplified([villa]);
              }
            }

            break;

          case PROJECT_ITEM_TYPE.BUILDING:
            const floor: FloorTypes =
              state[floorEntityStore].ids[state[sitePlanModelStore].floorId];

            let apartment: ProjectChildItemTypes;
            if (floor) {
              apartment = floor.items.find((i) => i.id === state[storeName].homeId);

              if (apartment) {
                homeKey = apartment.alias;
                dispatch[projectChildItemEntityStore].saveSimplified([apartment]);
              }
            }

            break;

          default:
            break;
        }
      }

      if (history) {
        dispatch[sitePlanModelStore].setRedirectDetailData({ history, childItemKey: homeKey });
      }
    },
    redirectToSitePlan(history) {
      if (history) {
        dispatch[sitePlanModelStore].goToSitePlanPage(history);
      }
    },
    getShowroomData({ data, activeKey }: { data: ProjectChildItemTypes; activeKey: string }) {
      let showroomData: ShowroomTypes[] = null;

      if (activeKey === CONTENT_KEY.HOME_PROFILE_PROMOTE.key) {
        showroomData = fetchPromoteData(data);
      } else {
        showroomData = fetchShowroomData(data);
      }

      if (showroomData.length) {
        dispatch[storeName].setShowroomData(showroomData);
        const tourData: { code: string; url: string }[] = [];
        showroomData.forEach((item) => {
          const tourUrl = item.url;
          const code = tourUrl.slice(tourUrl.lastIndexOf('/') + 1);
          if (!tourData.find((i) => i.code === code)) tourData.push({ code, url: item.url });
        });

        tourData.forEach((item) => {
          dispatch[tourEntityStore].loadByClientUrl(item);
        });
      }
    },

    async setHomeProfileData(
      payload: {
        currentActiveKey: string;
        projectKey: string;
        itemKey: string;
        floorKey: string;
        key: string;
      },
      state
    ) {
      const getHomeData = (data: ProjectChildItemTypes) => {
        this.getShowroomData({ data, activeKey: payload.currentActiveKey });

        // get data similar project child item
        if (!data.similarItem) {
          this.loadSimilarByKey({ id: data.id, key: data.code, page: 0 });
        }
        // get promotion data
        this.getPromotions(data.code);
      };

      const handleSetUserContactCodes = async (data: ProjectChildItemTypes) => {
        const userContactCodes: UserContactTypes[] = await dispatch[
          userContactEntity
        ].loadByCodeAndAlias({
          code: data.code,
          alias: data.alias,
        });

        const codes = userContactCodes.map((item) => item.code);
        this.setUserContactCodes(codes);
      };

      const { projectKey, itemKey, floorKey, key } = payload;
      // load data project
      const project: ProjectTypes & { notFound?: boolean } = await dispatch[
        projectModelStore
      ].checkProjectPublic({ projectKey });
      // check not found by project key
      if (!project || project.notFound || !project.items || !project.items.length) {
        return { notFound: true };
      }

      if (project.code) this.getAskedQuestions(project.code);

      if (state[storeName].homeKey) {
        const result = await dispatch[projectChildItemEntityStore].loadByKey(
          state[storeName].homeKey
        );
        if (result.notFound) return result;

        handleSetUserContactCodes(result);
        getHomeData(result);
        return;
      }

      // check current item key is child of founded project
      const simplifiedProjectItem = project.items.find(
        (i) => [i.alias, i.code].indexOf(itemKey) !== -1
      );
      if (!simplifiedProjectItem) return { notFound: true };

      // load data project item
      const projectItem: ProjectItemTypes & { notFound?: boolean } = await dispatch[
        projectItemEntityStore
      ].loadByKey(itemKey);

      // check not found by project item key
      if (!projectItem || projectItem.notFound || !project.items || !project.items.length) {
        return { notFound: true };
      }

      dispatch[sitePlanModelStore].setProjectItem({
        id: projectItem.id,
        type: projectItem.type,
        code: projectItem.code,
        key: itemKey,
      });

      let floor: FloorTypes;
      if (floorKey) {
        const simplifiedFloor = (projectItem.items as SimplifiedFloorTypes[]).find(
          (i) => [i.alias, i.code].indexOf(floorKey) !== -1
        );
        if (!simplifiedFloor) return { notFound: true };

        // fetch data floor
        floor = await dispatch[floorEntityStore].findById(simplifiedFloor.id);
        // store data floor key
        dispatch[sitePlanModelStore].setFloor({ id: floor.id, code: floor.code, key: floorKey });
      }

      // check current apartment key is child of founded floor
      if (floorKey && floor) {
        if (!floor.items || !floor.items.length) return { notFound: true };

        const apartment = floor.items.find((i) => i.alias === key || i.code === key);
        if (!apartment) return { notFound: true };
      }

      const result: ProjectChildItemTypes & { notFound?: boolean } = await dispatch[
        projectChildItemEntityStore
      ].loadByKey(key);

      // fetch data floor when current alias is assigned contact alias
      if (!floorKey && result && result.floor && result.floor.id) {
        // fetch data floor
        floor = await dispatch[floorEntityStore].findById(result.floor.id);
        // store data floor key
        dispatch[sitePlanModelStore].setFloor({ id: floor.id, code: floor.code, key: floor.alias });
      }

      // check not found by project child item key
      if (!result || result.notFound) return { notFound: true };

      // load data userContact
      handleSetUserContactCodes(result);
      this.setHomeData({ id: result.id, code: result.code, key });

      getHomeData(result);
    },
    setViewDetailShowroom(payload) {
      const { id, projectName, buildingName, floorName, floorNumber, apartmentName, url } = payload;

      dispatch[storeName].setViewDetailShowroomId(id);
      dispatch[storeName].openIframeContent({
        url,
        projectName,
        buildingName,
        floorName,
        floorNumber,
        apartmentName,
      });
    },
    resetHomeProfile() {
      // reset home profile state
      dispatch[storeName].setHomeData();
      dispatch[storeName].setShowroomData();
      dispatch[storeName].setViewDetailShowroomId();
      dispatch[storeName].setOpenContent();
      dispatch[storeName].setActiveAgent(null);
    },
    async loadSimilarByKey(payload) {
      const { id, key, page = 0 } = payload;
      await dispatch[projectChildItemEntityStore].loadSimilarByKey({ id, key, page });
    },
    setRedirectSimilarProjectChildItem(payload, state) {
      const { floorKey, key, link = HOME_PROFILE_PAGE_LINK } = payload;
      const { projectKey } = state[projectModelStore];
      const { projectItemKey } = state[sitePlanModelStore];

      let extendable = key;
      if (floorKey) extendable = `${floorKey}--${key}`;

      const homeProfileLink = dispatch[projectModelStore]
        .getHomePageLink({ link })
        .replace(':projectKey', projectKey)
        .replace(':itemKey', projectItemKey)
        .replace(':extendable', extendable);

      if (homeProfileLink) {
        window.open(`${process.env.REACT_APP_DOMAIN}${homeProfileLink}`, '_blank');
      }
    },
    async getPromotions(payload: string) {
      const promotions: PromotionResponseTypes[] = await dispatch[promotionEntityStore].loadByKey(
        payload
      );
      const promotionCodes = promotions.map((item) => item.code);

      this.setPromotionCodes(promotionCodes);
    },

    async getAskedQuestions(payload: string) {
      const questions: QuestionType[] = await dispatch[questionsEntityStore].loadByKey({
        key: payload,
      });
      if (!questions || !questions.length) return;

      const codes = questions.map((item) => item.code);
      if (codes && codes.length) this.setAskedQuestionCodes(codes);
    },

    onViewPromotionDetail(payload: PromotionsTypes) {
      const { url, code, type, parentCode, description } = payload;

      if (url) {
        window.open(url);
      } else if (description) {
        this.setStatusPromotionPopup(true);
        this.setPromotionDetail({ code, type, parentCode });
      }
    },
    openIframeContent(payload, state) {
      if (!payload) {
        dispatch[projectModelStore].setIframeContent();
        return;
      }

      const {
        projectCode = state[projectModelStore].projectCode,
        projectName,
        buildingCode = state[sitePlanModelStore].projectItemCode,
        buildingName,
        floorCode = state[sitePlanModelStore].floorCode,
        floorName,
        floorNumber,
        apartmentCode = state[storeName].homeCode,
        apartmentName,
        url,
      } = payload;

      if (!url) {
        dispatch[projectModelStore].setIframeContent();
        return;
      }

      const hashParams = getHashParams();

      if (hashParams && !Object.keys(hashParams).includes('vt')) {
        buildHashUrl({
          ...hashParams,
          vt: url,
        });
      }

      // add logic handle generate url virtual tour for tracking
      const temp = new URL(url);

      const transform360ContentType = (): string => {
        const data: ShowroomTypes = (state[storeName].showroomData as ShowroomTypes[]).find(
          (item) => item.id === state[storeName].viewDetailShowroom
        );

        if (!data || data.title.indexOf('standard') !== -1) return 'unfurnished';
        return 'fully-furnished';
      };

      const contentType360 = transform360ContentType();

      if (projectCode) temp.searchParams.set('project', projectCode);
      if (projectName) temp.searchParams.set('project_name', projectName);
      if (buildingCode) temp.searchParams.set('building', buildingCode);
      if (buildingName) temp.searchParams.set('building_name', buildingName);
      if (floorCode) temp.searchParams.set('floor', floorCode);
      if (floorName) temp.searchParams.set('floor_name', floorName);
      if (floorNumber) temp.searchParams.set('floor_number', floorNumber);
      if (apartmentCode) temp.searchParams.set('apartment', apartmentCode);
      if (apartmentName) temp.searchParams.set('apartment_name', apartmentName);
      if (contentType360) temp.searchParams.set('360_content_type', contentType360);

      // add url hash param vt360
      temp.hash = temp.hash.startsWith('#')
        ? `&url=${window.location.href}`
        : `#url=${window.location.href}`;

      dispatch[projectModelStore].setIframeContent({ visible: true, url: temp.href });
    },
    async setSelfHomeProfileData(payload) {
      const getHomeData = (data: ProjectChildItemTypes) => {
        this.getShowroomData({ data, activeKey: payload.currentActiveKey });

        // get data similar project child item
        if (!data.similarItem) {
          this.loadSimilarByKey({ id: data.id, key: data.code, page: 0 });
        }
        // get promotion data
        this.getPromotions(data.code);
      };

      const result = await dispatch[projectChildItemEntityStore].loadSelfByKey(payload.itemKey);

      // check not found by project child item key
      if (!result || result.notFound) return { notFound: true };

      const { item, simplifiedItem } = result;

      if (!!simplifiedItem && !!simplifiedItem.project) {
        const { id, code, alias } = simplifiedItem.project;

        // store data project
        dispatch[projectEntityStore].loadByKey(code);
        dispatch[projectModelStore].setProject({ id, code, key: alias || code });
        if (code) this.getAskedQuestions(code);
      }

      if (!!simplifiedItem && !!simplifiedItem.projectItem) {
        const { id, code, alias, type } = simplifiedItem.projectItem;

        // store data project item
        dispatch[projectItemEntityStore].storeProjectItemTemp(simplifiedItem.projectItem);
        dispatch[sitePlanModelStore].setProjectItem({ id, type, code, key: alias || code });
      }

      if (!!simplifiedItem && !!simplifiedItem.floor) {
        const { id, code, alias } = simplifiedItem.project;

        // store data floor
        dispatch[floorEntityStore].storeProjectItemTemp(simplifiedItem.floor);
        dispatch[sitePlanModelStore].setFloor({ id, code, key: alias || code });
      }

      this.setHomeData({ id: item.id, code: item.code, key: payload.itemKey, isSelf: true });
      getHomeData(item);
    },
  }),
});

export default homeProfileModel;

// get data for home profile promote showrooms
const fetchPromoteData = (result: ProjectChildItemTypes): ShowroomTypes[] => {
  // check promote showroom and generate promote showroom data
  // TODO: MUST BE UPDATE AFTER CONFIG DATA SHOW ROOM ON CMS
  const data: ShowroomTypes[] = [];
  if (result.furnitureUrl) {
    data.push({
      title: 'home-profile.showroom.furniture-residence.title',
      url: result.furnitureUrl,
      description: 'home-profile.showroom.furniture-residence.description',
      backgroundImage:
        'https://clikhub-v1.s3.ap-southeast-1.amazonaws.com/prod/Microsoft_Teams_image_27_4541befd76.png',
      bedroom: result.bedroom,
      bathroom: result.bathroom,
      square: result.square,
      direction: result.direction,
      id: HOME_PROFILE_FEATURE_ACTIONS.HOME_FURNITURE.variableId,
    });
  }

  return data;
};

// get data for home profile showroom
const fetchShowroomData = (result: ProjectChildItemTypes): ShowroomTypes[] => {
  // check showroom and generate showroom data
  // TODO: MUST BE UPDATE AFTER CONFIG DATA SHOWROOM ON CMS
  const data: ShowroomTypes[] = [];
  if (result.virtualTourUrl) {
    data.push({
      title: 'home-profile.showroom.standard-residence.title',
      url: result.virtualTourUrl,
      description: 'home-profile.showroom.standard-residence.description',
      backgroundImage:
        'https://clikhub-v1.s3.ap-southeast-1.amazonaws.com/prod/Microsoft_Teams_image_26_64baf503a3.png',
      bedroom: result.bedroom,
      bathroom: result.bathroom,
      square: result.square,
      usableSquare: result.usableSquare,
      direction: result.direction,
      id: HOME_PROFILE_FEATURE_ACTIONS.VIEW_360.variableId,
    });
  }

  if (result.furnitureUrl) {
    data.push({
      title: 'home-profile.showroom.furniture-residence.title',
      url: result.furnitureUrl,
      description: 'home-profile.showroom.furniture-residence.description',
      backgroundImage:
        'https://clikhub-v1.s3.ap-southeast-1.amazonaws.com/prod/Microsoft_Teams_image_27_4541befd76.png',
      bedroom: result.bedroom,
      bathroom: result.bathroom,
      square: result.square,
      usableSquare: result.usableSquare,
      direction: result.direction,
      id: HOME_PROFILE_FEATURE_ACTIONS.HOME_FURNITURE.variableId,
    });
  }

  return data;
};
